import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/comics';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + total) % total);
  }, [total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-xl">
            {heroSlides[current].badge && (
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-4">
                {heroSlides[current].badge}
              </span>
            )}
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">
              {heroSlides[current].title}
            </h2>
            <p className="text-base md:text-lg text-white/80 mb-6">
              {heroSlides[current].subtitle}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/article/${heroSlides[current].comicSlug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即阅读
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer border border-white/30"
              >
                浏览全部
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors cursor-pointer border border-white/20"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors cursor-pointer border border-white/20"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              index === current
                ? 'bg-white w-6'
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
}